import type { CSSProperties, ReactNode } from 'react';
import { APP } from './tokens';
import type { PlacementType } from '../pdfViewer/AddFieldToolbar';

export interface SigningGuideSigner {
  email: string;
  name: string;
  role: string;
}

interface SigningGuideCardProps {
  signers: SigningGuideSigner[];
  activeSignerIndex: number;
  placementMode: PlacementType | null;
  selfMarkerCount: number;
  selfApplied: boolean;
  recipientMarkerCount: number;
  sendDisabled?: boolean;
  onSelectSigner: (index: number) => void;
  onPlaceField: (type: PlacementType, signerIndex: number) => void;
  onCancelPlacement: () => void;
  onAddRecipientEmail: (email: string) => void;
  onRemoveRecipientEmail: (email: string) => void;
  onSendForSignature: () => void;
}

const FIELD_TYPES: Array<{ type: PlacementType; label: string }> = [
  { type: 'signature', label: 'Signature' },
  { type: 'date', label: 'Date' },
  { type: 'name', label: 'Name' },
  { type: 'initial', label: 'Initial' },
  { type: 'title', label: 'Title' },
];

const fieldLabel = (type: PlacementType | null) => {
  if (!type) return 'field';
  if (type === 'initial') return 'initial';
  return type;
};

export default function SigningGuideCard({
  signers,
  activeSignerIndex,
  placementMode,
  selfMarkerCount,
  selfApplied,
  recipientMarkerCount,
  sendDisabled,
  onSelectSigner,
  onPlaceField,
  onCancelPlacement,
  onRemoveRecipientEmail,
  onSendForSignature,
}: SigningGuideCardProps) {
  const selfSigner = signers[0] ?? { name: 'You', email: 'self', role: 'self' };
  const recipientSigners = signers.slice(1);
  const activeSigner = signers[activeSignerIndex] ?? selfSigner;
  const activeIsRecipient = activeSignerIndex > 0 || activeSigner.role === 'other';
  const activeChipSignerIndex = Math.max(0, Math.min(activeSignerIndex, Math.max(0, signers.length - 1)));
  const hasRealRecipientEmail = (email: string) => /\S+@\S+\.\S+/.test(email.trim());

  const chipStyle = (armed: boolean): CSSProperties => ({
    height: 32,
    padding: '0 11px',
    borderRadius: 8,
    border: `1px solid ${armed ? APP.bgChatSendBtn : APP.border}`,
    background: armed ? APP.bgWorkflowIcon : APP.bgCardBody,
    color: armed ? APP.cornerGradientEnd : APP.textPrimary,
    fontFamily: APP.fontFamily,
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  });

  const primaryButton = (disabled?: boolean): CSSProperties => ({
    height: 36,
    borderRadius: 8,
    border: `1px solid ${disabled ? APP.border : APP.bgChatSendBtn}`,
    background: disabled ? APP.bgCardBody : APP.bgChatSendBtn,
    color: disabled ? APP.textMuted : '#FFFFFF',
    fontFamily: APP.fontFamily,
    fontSize: 13,
    fontWeight: 750,
    cursor: disabled ? 'not-allowed' : 'pointer',
  });

  const secondaryButton: CSSProperties = {
    height: 32,
    borderRadius: 8,
    border: `1px solid ${APP.border}`,
    background: APP.bgCardBody,
    color: APP.textPrimary,
    fontFamily: APP.fontFamily,
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  };

  const helpStyle: CSSProperties = {
    color: APP.textSecondary,
    fontFamily: APP.fontFamily,
    fontSize: 12,
    lineHeight: '17px',
  };

  const titleStyle: CSSProperties = {
    color: APP.textPrimary,
    fontFamily: APP.fontFamily,
    fontSize: 14,
    fontWeight: 800,
    lineHeight: '18px',
  };

  const renderFieldChips = (signerIndex: number) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {FIELD_TYPES.map(({ type, label }) => (
        <button
          key={`${signerIndex}-${type}`}
          type="button"
          onClick={() => onPlaceField(type, signerIndex)}
          style={chipStyle(placementMode === type && activeSignerIndex === signerIndex)}
        >
          {label}
        </button>
      ))}
    </div>
  );

  let body: ReactNode;
  if (placementMode) {
    body = (
      <>
        <div style={titleStyle}>Click the document</div>
        <div style={helpStyle}>
          {`Place ${activeIsRecipient ? `${activeSigner.name || 'the recipient'}'s` : 'your'} ${fieldLabel(placementMode)} where it belongs.`}
        </div>
        <button type="button" onClick={onCancelPlacement} style={secondaryButton}>
          Cancel placement
        </button>
      </>
    );
  } else if (selfMarkerCount === 0) {
    body = (
      <>
        <div style={titleStyle}>Place your signature field</div>
        <div style={helpStyle}>Choose Signature, then click the document exactly where your signature belongs.</div>
        {renderFieldChips(0)}
      </>
    );
  } else if (!selfApplied) {
    body = (
      <>
        <div style={titleStyle}>Apply your changes</div>
        <div style={helpStyle}>
          {`${selfMarkerCount} field${selfMarkerCount === 1 ? '' : 's'} placed for ${selfSigner.name || 'you'}. When positions look right, use Apply changes in the panel header above.`}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={helpStyle}>Need another field?</div>
          {renderFieldChips(activeChipSignerIndex)}
        </div>
      </>
    );
  } else if (recipientMarkerCount === 0) {
    const recipientIndex = Math.max(1, activeSignerIndex || 1);
    body = (
      <>
        <div style={titleStyle}>Place recipient fields</div>
        <div style={helpStyle}>Choose who needs to sign, then place their required fields on the document.</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {recipientSigners.map((signer, index) => {
            const signerIndex = index + 1;
            const selected = activeSignerIndex === signerIndex;
            return (
              <button
                key={`${signer.email}-${signerIndex}`}
                type="button"
                onClick={() => onSelectSigner(signerIndex)}
                style={chipStyle(selected)}
              >
                {signer.name || signer.email}
              </button>
            );
          })}
        </div>
        {renderFieldChips(recipientIndex)}
      </>
    );
  } else {
    body = (
      <>
        <div style={titleStyle}>Ready to send</div>
        <div style={helpStyle}>
          {`${recipientMarkerCount} recipient field${recipientMarkerCount === 1 ? '' : 's'} placed. Send when the document looks right.`}
        </div>
        <button type="button" disabled={sendDisabled} onClick={onSendForSignature} style={primaryButton(sendDisabled)}>
          Send for signature
        </button>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {recipientSigners.map((signer) => (
            <span
              key={signer.email}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 28,
                padding: '0 8px',
                borderRadius: 999,
                border: `1px solid ${APP.border}`,
                background: APP.bgCardBody,
                color: APP.textSecondary,
                fontFamily: APP.fontFamily,
                fontSize: 12,
                fontWeight: 650,
              }}
            >
              {signer.name || signer.email}
              {hasRealRecipientEmail(signer.email) && (
                <button
                  type="button"
                  onClick={() => onRemoveRecipientEmail(signer.email)}
                  style={{ border: 'none', background: 'none', color: APP.textMuted, cursor: 'pointer', padding: 0 }}
                  aria-label={`Remove ${signer.email}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
        {renderFieldChips(Math.max(1, activeSignerIndex || 1))}
      </>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        padding: 12,
        borderRadius: 12,
        border: `1px solid ${placementMode ? APP.bgChatSendBtn : APP.borderCard}`,
        background: APP.bgContent,
        boxShadow: placementMode ? '0 0 0 3px rgba(196, 98, 42, 0.15)' : APP.inputShadow,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: placementMode ? APP.bgChatSendBtn : selfApplied ? APP.textGreen : APP.textMuted,
            flexShrink: 0,
          }}
        />
        <div style={{ color: APP.textMuted, fontFamily: APP.fontFamily, fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Signing guide
        </div>
      </div>
      {body}
    </div>
  );
}
